import { useMemo } from "react"
import { numberToCurrency } from "helpers/string"

const useSummaryDetails = (paymentSummary, customFees = [], { combineFees = false } = {}) => {
  const { tax_and_fee, adult, child, infant, passenger, pricing_type, extra_adult, extra_child, extra_infant, extra_passenger, flat_rate, custom_fees: paymentCustomFees, discount_amount, discount_code } =
    paymentSummary ?? {}

  return useMemo(() => {
    let subtotal = 0

    const details = []

    const addItem = (labelKey, item) => {
      if (item?.count && item?.price) {
        details.push({ labelKey, count: item.count, value: item.price })
        subtotal += item.price ?? 0
      }
    }
    if (pricing_type === "charge_per_person") {
      const items = [
        { labelKey: "client.payment.labels.adult_x", item: adult },
        { labelKey: "client.payment.labels.child_x", item: child },
        { labelKey: "client.payment.labels.infant_x", item: infant },
        { labelKey: "client.payment.labels.passenger_x", item: passenger }
      ]
      for (const { labelKey, item } of items) addItem(labelKey, item)
    } else if (pricing_type === "charge_flat_rate") {
      details.push({ labelKey: "client.payment.labels.flat_rate_for_included_guests", value: flat_rate ?? 0 })
      subtotal += flat_rate ?? 0
      const extraItems = [
        { labelKey: "client.payment.labels.extra_adult_x", item: extra_adult },
        { labelKey: "client.payment.labels.extra_child_x", item: extra_child },
        { labelKey: "client.payment.labels.extra_infant_x", item: extra_infant },
        { labelKey: "client.payment.labels.extra_passenger_x", item: extra_passenger }
      ]
      for (const { labelKey, item } of extraItems) addItem(labelKey, item)
    }

    details.push({ labelKey: "client.payment.labels.subtotal", value: subtotal, fw: 600 })

    if (discount_amount > 0) {
      const discountLabel = discount_code ? `Discount (${discount_code})` : "Discount"
      details.push({ label: discountLabel, value: -discount_amount, isDiscount: true })
    }

    const feesToUse = paymentCustomFees ?? customFees

    if (combineFees) {
      const infoItems = [
        ...feesToUse.map((f) => ({ label: f.title, amount: numberToCurrency(Number(f.price)) })),
        ...((tax_and_fee ?? 0) > 0 ? [{ label: "Platform Fee", amount: numberToCurrency(tax_and_fee) }] : [])
      ]
      const combinedTotal = feesToUse.reduce((sum, f) => sum + Number(f.price), 0) + (tax_and_fee ?? 0)
      if (combinedTotal > 0) {
        details.push({
          label: "Tax & Fees",
          value: combinedTotal,
          combinedFee: true,
          info: infoItems.length > 1 ? infoItems : undefined
        })
      }
    } else {
      for (const fee of feesToUse) {
        details.push({ label: fee.title, value: Number(fee.price) })
      }
      details.push({ labelKey: "client.payment.labels.taxes_fees", value: tax_and_fee ?? 0 })
    }

    return details
  }, [adult, child, infant, passenger, tax_and_fee, flat_rate, extra_adult, extra_child, extra_infant, extra_passenger, pricing_type, paymentCustomFees, customFees, combineFees, discount_amount, discount_code])
}

export default useSummaryDetails
